import {
  ReactNode,
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from 'react';

type AudioItem = {
  itemId: string;
  audioUrl?: string | null;
};

type InteractiveAudioContextType = {
  audioList: AudioItem[];
  currentAudioId: string | null;
  isPlaying: boolean;
  isLoading: boolean;
  playAudio: (id: string) => void;
  pauseAudio: () => void;
  setAudioList: (list: AudioItem[]) => void;
};

const noop = () => {};

export const InteractiveAudioContext =
  createContext<InteractiveAudioContextType>({
    audioList: [],
    currentAudioId: null,
    isPlaying: false,
    isLoading: false,
    playAudio: noop,
    pauseAudio: noop,
    setAudioList: noop,
  });

export const InteractiveAudioProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const audioCache = useRef<Map<string, HTMLAudioElement>>(new Map());
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  const [audioList, setAudioList] = useState<AudioItem[]>([]);
  const [currentAudioId, setCurrentAudioId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const cleanUpAudio = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.removeEventListener('play', handlePlay);
      currentAudioRef.current.removeEventListener('pause', handlePause);
      currentAudioRef.current.removeEventListener('ended', handleEnded);
      currentAudioRef.current.removeEventListener(
        'canplaythrough',
        handleCanPlayThrough
      );
      currentAudioRef.current = null;
    }
  };

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  const handleEnded = () => setIsPlaying(false);
  const handleCanPlayThrough = () => setIsLoading(false);

  const playAudio = (id: string) => {
    const audioItem = audioList.find(item => item.itemId === id);
    if (!audioItem) return;

    // Cleanup previous instance
    cleanUpAudio();

    let audio = audioCache.current.get(id);
    if (!audio && audioItem.audioUrl) {
      // Cache unplayed audio
      audio = new Audio(audioItem.audioUrl);
      audioCache.current.set(id, audio);
    }

    if (audio) {
      setIsLoading(true);

      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('canplaythrough', handleCanPlayThrough, {
        once: true,
      });

      currentAudioRef.current = audio;
      setCurrentAudioId(id);
      audio.play().catch(() => setIsLoading(false)); // Stop loading if play fails
    }
  };

  const pauseAudio = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
    }
  };

  useEffect(() => {
    return () => cleanUpAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <InteractiveAudioContext.Provider
      value={{
        audioList,
        currentAudioId,
        isPlaying,
        isLoading,
        playAudio,
        pauseAudio,
        setAudioList,
      }}
    >
      {children}
    </InteractiveAudioContext.Provider>
  );
};

export const useInteractiveAudioContext = () =>
  useContext(InteractiveAudioContext);
